import { useEffect, useState } from 'react';
import { Mic, Copy, FileAudio, Clock, Zap, Crown, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';

const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuthStore();
  const [greeting, setGreeting] = useState('');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting(t('greeting.morning'));
    else if (hour < 18) setGreeting(t('greeting.afternoon'));
    else setGreeting(t('greeting.evening'));
  }, [t]);

  const features = [
    {
      title: t('features.voiceDesign.title'),
      description: t('features.voiceDesign.description'),
      icon: Mic,
      path: '/voice-design',
      color: 'bg-blue-500',
    },
    {
      title: t('features.voiceClone.title'),
      description: t('features.voiceClone.description'),
      icon: Copy,
      path: '/voice-clone',
      color: 'bg-purple-500',
    },
    {
      title: t('features.transcribe.title'),
      description: t('features.transcribe.description'),
      icon: FileAudio,
      path: '/transcribe',
      color: 'bg-green-500',
    },
  ];

  return (
    <div className="space-y-8">
      {/* VIP 升级引导 - 非 VIP 非 admin 用户显示 */}
      {user?.role !== 'vip' && user?.role !== 'admin' && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Crown className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-900">{t('vipBanner.title')}</p>
              <p className="text-sm text-amber-700 mt-0.5">{t('vipBanner.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {t('vipBanner.viewPlans')}
            </Link>
            <a
              href="mailto:aivoice@qq.com?subject=VIP会员开通申请"
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-amber-300 hover:border-amber-400 text-amber-700 hover:text-amber-800 text-sm font-medium rounded-lg transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              {t('vipBanner.contactUs')}
            </a>
          </div>
        </div>
      )}

      {/* 欢迎区域 */}
      <div className="card bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <h1 className="text-3xl font-bold mb-2">
          {t('greeting.template', { greeting, username: user?.username })}
        </h1>
        <p className="text-primary-100">
          {t('welcome')}
        </p>
      </div>

      {/* 配额信息 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('quota.dailyQuota')}</p>
              <p className="text-2xl font-bold">{user?.daily_quota}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Clock className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('quota.usedToday')}</p>
              <p className="text-2xl font-bold">{user?.used_today}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Mic className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('quota.remaining')}</p>
              <p className="text-2xl font-bold">{user?.remaining}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 功能卡片 */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('features.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Link
                key={feature.path}
                to={feature.path}
                className="card hover:shadow-lg transition-shadow group"
              >
                <div className={`inline-flex p-3 rounded-lg ${feature.color} text-white mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* 使用说明 */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('usageGuide.title')}</h2>
        <div className="space-y-3 text-sm text-gray-600">
          <p dangerouslySetInnerHTML={{ __html: t('usageGuide.step1') }} />
          <p dangerouslySetInnerHTML={{ __html: t('usageGuide.step2') }} />
          <p dangerouslySetInnerHTML={{ __html: t('usageGuide.step3') }} />
          <p dangerouslySetInnerHTML={{ __html: t('usageGuide.step4') }} />
          <p className="text-primary-600 mt-4">
            💡 {t('usageGuide.tip')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
